import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BottomNav } from "@/components/Nav";
import { Link } from "react-router-dom";
import { Flame, Dumbbell, TrendingDown, CheckCircle2 } from "lucide-react";

function ClientCoachNotes() {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  useEffect(() => {
    api.get(`/coach/notes?client_id=${user.id}`).then(r=>setNotes(r.data)).catch(()=>{});
  }, [user.id]);
  if (!notes.length) return null;
  return (
    <div className="mt-5 kw-card p-4" data-testid="client-coach-notes">
      <div className="text-xs uppercase tracking-widest text-gold">From your coach</div>
      {notes.slice(-3).reverse().map(n => (
        <div key={n.id} className="mt-3 border-l-2 border-gold pl-3">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Week {n.week}</div>
          <div className="text-sm text-neutral-200 mt-1">{n.content}</div>
        </div>
      ))}
    </div>
  );
}

function todayStr() { return new Date().toISOString().slice(0,10); }

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [weights, setWeights] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const date = todayStr();

  useEffect(() => {
    (async () => {
      const [s, w, c, wk] = await Promise.all([
        api.get(`/food-log/summary?date=${date}`),
        api.get("/progress/weight"),
        api.get("/checkins"),
        api.get("/warrior-weeks"),
      ]);
      setSummary(s.data); setWeights(w.data); setCheckins(c.data); setWeeks(wk.data);
    })();
  }, [date]);

  const latestWeight = weights[weights.length-1];
  const startWeight = weights[0];
  const delta = latestWeight && startWeight ? (latestWeight.weight_kg - startWeight.weight_kg) : 0;
  const todayCheckin = checkins.find(c=>c.date===date);
  const weekNumber = Math.min(12, Math.max(1, Math.floor((checkins.length + 6)/7) || 1));
  const currentWeek = weeks.find(w=>w.week===weekNumber) || weeks[0];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white safe-pb">
      <div className="max-w-xl mx-auto px-4 pt-6">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-gold uppercase tracking-[0.3em] text-[10px]">Warrior</div>
            <h1 className="font-display font-black uppercase text-3xl tracking-tighter">Kia ora, {user?.name?.split(" ")[0]}</h1>
          </div>
          <Link to="/app/checkin" className="kw-btn-primary text-xs" data-testid="dash-checkin-cta">Daily check-in</Link>
        </div>

        {currentWeek && (
          <div className="kw-card mt-5 p-4" data-testid="week-banner">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500">Week {currentWeek.week} · {currentWeek.theme}</div>
                <div className="text-base mt-1">{currentWeek.focus}</div>
              </div>
              <div className="font-display font-black text-4xl text-gold leading-none">W{currentWeek.week}</div>
            </div>
          </div>
        )}

        {summary && (
          <div className="kw-card mt-5 p-4" data-testid="macro-summary">
            <div className="flex justify-between items-end">
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500">Today's fuel</div>
                <div className="font-display font-black text-4xl">{Math.round(summary.totals.calories)}<span className="text-base text-neutral-500"> / {summary.targets.calories} kcal</span></div>
              </div>
              <Link to="/app/nutrition" className="text-gold uppercase tracking-widest text-xs">Log meal →</Link>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2">
              {[
                ["Protein", summary.totals.protein_g, summary.targets.protein_g, "g"],
                ["Carbs", summary.totals.carbs_g, summary.targets.carbs_g, "g"],
                ["Fat", summary.totals.fat_g, summary.targets.fat_g, "g"],
              ].map(([k, v, t, u]) => (
                <div key={k} className="border border-[#262626] p-2">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">{k}</div>
                  <div className="font-display font-bold text-xl">{Math.round(v)}<span className="text-xs text-neutral-500">/{Math.round(t)}{u}</span></div>
                  <div className="h-1 bg-[#262626] mt-1"><div className="h-1 bg-gold" style={{width: `${Math.min(100,(v/t)*100)}%`}}/></div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-5 grid grid-cols-2 gap-3">
          <Link to="/app/progress" className="kw-card p-4" data-testid="dash-progress-card">
            <TrendingDown className="w-5 h-5 text-gold"/>
            <div className="text-xs uppercase tracking-widest text-neutral-500 mt-2">Weight change</div>
            <div className="font-display font-black text-2xl mt-1">{delta>0?"+":""}{delta.toFixed(1)} kg</div>
            <div className="text-[10px] text-neutral-500 uppercase tracking-widest">{latestWeight?.weight_kg} kg today</div>
          </Link>
          <Link to="/app/workouts" className="kw-card p-4" data-testid="dash-workout-card">
            <Dumbbell className="w-5 h-5 text-gold"/>
            <div className="text-xs uppercase tracking-widest text-neutral-500 mt-2">Workouts</div>
            <div className="font-display font-black text-2xl mt-1">View plan</div>
            <div className="text-[10px] text-neutral-500 uppercase tracking-widest">Weekly strength + walking</div>
          </Link>
        </div>

        <div className="mt-5 kw-card p-4">
          <div className="flex justify-between items-center">
            <div className="text-xs uppercase tracking-widest text-neutral-500">Today's discipline</div>
            {todayCheckin ? <CheckCircle2 className="w-5 h-5 text-gold" data-testid="checkin-done"/> : <span className="text-xs text-neutral-500">Not done</span>}
          </div>
          {todayCheckin ? (
            <div className="mt-2 text-sm text-neutral-300">Discipline score <span className="text-gold font-bold">{todayCheckin.discipline_score}/10</span> · Sleep {todayCheckin.sleep_hours}h · Water {todayCheckin.water_litres}L</div>
          ) : (
            <Link to="/app/checkin" className="mt-2 inline-block text-gold text-sm uppercase tracking-widest">Complete today's check-in →</Link>
          )}
        </div>

        <div className="mt-5 kw-card p-4">
          <Flame className="w-5 h-5 text-gold"/>
          <div className="text-xs uppercase tracking-widest text-neutral-500 mt-2">Transformation reminder</div>
          <p className="text-sm text-neutral-300 mt-1">Estimates are not guarantees. Discipline, sleep and consistency do the heavy lifting. Lead by example — your whānau is watching.</p>
          <p className="text-[11px] text-neutral-500 mt-2">Your calorie target is set by your onboarding profile and is <span className="text-gold">not</span> reduced by exercise. Train hard, eat your target.</p>
        </div>
        <ClientCoachNotes/>
      </div>
      <BottomNav />
    </div>
  );
}
